import type { RealtimeChannel } from '@supabase/supabase-js';
import { TopicHealth, isTopicRefusal, type ConnectionStatus } from '@tradeoutlet/shared';
import { connection } from './connection';
import { supabase } from './supabase';

export type RealtimeHandler = (event: string, payload: Record<string, unknown>) => void;
/** Live updates: 'polling' in local dev without Supabase. */
export type ConnectionState = 'connecting' | 'live' | 'reconnecting' | 'polling';
export type { ConnectionStatus };

interface Entry { channel: RealtimeChannel | null; handlers: Set<RealtimeHandler>; erroredAt: number | null; dropTimer: ReturnType<typeof setTimeout> | null }

/** t574u: a topic the server refused is tried again this often (permissions can change). */
export const REFUSED_RETRY_MS = 5 * 60_000;
/** t574u: a channel still errored this long after the socket is back is thrown away and joined afresh. */
export const STUCK_CHANNEL_MS = 30_000;
/**
 * t574u: the last listener going away leaves the topic this long before the channel is closed, so an effect that
 * re-runs (the conversation list changing re-subscribes every chat topic) keeps the live channel instead of getting
 * back the one that is still leaving, which never joins and silently drops that conversation's updates.
 */
export const LEAVE_DELAY_MS = 1_000;

/**
 * Supabase Realtime private Broadcast subscriptions, ref-counted per topic.
 * Topics are authorised server-side by RLS on realtime.messages (collab_rt.can_access_topic).
 * Delivery is at-least-once and can drop during reconnects, so every (re)subscribe fires
 * `onSubscribed` listeners which run after_seq gap recovery. Without Supabase (local dev)
 * the hub reports 'polling' and callers poll the API instead.
 *
 * t574u: what the connection indicator shows comes from `monitor` (packages/shared ConnectionMonitor): per-topic
 * health (a refused topic no longer counts as "disconnected"), the socket heartbeat, the browser's online signal and
 * every API response, with a grace period so brief blips never show.
 */
class RealtimeHub {
  readonly mode: 'realtime' | 'polling' = supabase ? 'realtime' : 'polling';
  private entries = new Map<string, Entry>();
  private health = new TopicHealth();
  private subscribedListeners = new Set<(topic: string) => void>();
  private stateListeners = new Set<(s: ConnectionState) => void>();
  private recoveredListeners = new Set<() => void>();
  private state: ConnectionState = supabase ? 'connecting' : 'polling';
  private everLive = false;
  private refusedTimer: ReturnType<typeof setInterval> | null = null;
  /** Channels being closed, by topic: a new join for the topic waits for the close to finish. */
  private leaving = new Map<string, Promise<unknown>>();
  readonly monitor = connection;

  constructor() {
    if (supabase) {
      supabase.realtime.onHeartbeat(status => {
        if (status === 'ok') { this.health.heartbeat(true); this.monitor.contact(); this.recompute(); }
        else if (status === 'timeout' || status === 'error') { this.health.heartbeat(false); this.recompute(); }
      });
    }
  }

  getState() { return this.state; }
  onState(fn: (s: ConnectionState) => void) { this.stateListeners.add(fn); return () => { this.stateListeners.delete(fn); }; }
  /** What the indicator shows now. */
  getStatus(): ConnectionStatus { return this.monitor.getStatus(); }
  onStatus(fn: (s: ConnectionStatus) => void) { return this.monitor.subscribe(fn); }
  /** Live updates came back after being down: refetch what may have been missed. */
  onRecovered(fn: () => void) { this.recoveredListeners.add(fn); return () => { this.recoveredListeners.delete(fn); }; }

  /** An API response (any status the server itself sent) proves we're connected. */
  noteContact() { this.monitor.contact(); }

  private setState(s: ConnectionState) {
    if (this.state === s) return;
    this.state = s;
    this.stateListeners.forEach(l => l(s));
  }

  /** Work out live / reconnecting from every open topic now (never from the last event alone). */
  private recompute() {
    if (!supabase) return;
    const wasDown = this.monitor.isRealtimeDown();
    const down = this.health.isDown();
    const anyJoined = [...this.entries.keys()].some(t => this.health.get(t) === 'joined');
    if (down) this.setState(this.everLive ? 'reconnecting' : 'connecting');
    else if (anyJoined || this.everLive) this.setState('live');
    this.monitor.setRealtimeUp(!down);
    if (wasDown && !down) this.recoveredListeners.forEach(l => l());
  }

  /** Called after every successful (re)subscribe to a topic - use it to catch up. */
  onSubscribed(fn: (topic: string) => void) { this.subscribedListeners.add(fn); return () => { this.subscribedListeners.delete(fn); }; }

  /** Close a channel; a join for the same topic waits until it has gone (supabase.channel() would hand it back). */
  private drop(topic: string, ch: RealtimeChannel) {
    if (!supabase) return;
    const p: Promise<unknown> = supabase.removeChannel(ch).catch(() => undefined).finally(() => { if (this.leaving.get(topic) === p) this.leaving.delete(topic); });
    this.leaving.set(topic, p);
  }

  private join(topic: string, entry: Entry) {
    if (!supabase) return;
    entry.channel = null;
    const pending = this.leaving.get(topic);
    if (pending) {
      this.health.set(topic, 'joining');
      void pending.then(() => { if (this.entries.get(topic) === entry && entry.channel === null) this.join(topic, entry); });
      return;
    }
    const stale = supabase.getChannels().find(c => c.topic === `realtime:${topic}`);
    if (stale) { this.drop(topic, stale); this.join(topic, entry); return; }
    const ch = supabase.channel(topic, { config: { private: true, broadcast: { self: false, ack: false } } });
    ch.on('broadcast', { event: '*' }, msg => {
      if (entry.channel !== ch) return;
      // A message proves the socket and this topic are fine.
      this.monitor.contact();
      if (this.health.get(topic) !== 'joined') { this.health.set(topic, 'joined'); entry.erroredAt = null; this.recompute(); }
      const payload = (msg.payload ?? {}) as Record<string, unknown>;
      entry.handlers.forEach(h => h(msg.event, payload));
    });
    this.health.set(topic, 'joining');
    ch.subscribe((status, err) => {
      if (entry.channel !== ch || this.entries.get(topic) !== entry) return; // an old channel we replaced
      if (status === 'SUBSCRIBED') {
        this.everLive = true;
        entry.erroredAt = null;
        this.health.set(topic, 'joined');
        this.monitor.contact();
        this.recompute();
        this.subscribedListeners.forEach(l => l(topic));
      } else if (status === 'CHANNEL_ERROR' && isTopicRefusal(err)) {
        // The server's topic rules refused this topic (e.g. a task with no project). Not a connection problem: stop
        // the rejoin loop and try again later.
        this.health.set(topic, 'refused');
        entry.channel = null;
        this.drop(topic, ch);
        this.armRefusedRetry();
        this.recompute();
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        if (entry.erroredAt === null) entry.erroredAt = Date.now();
        this.health.set(topic, 'errored');
        this.recompute();
      } else if (status === 'CLOSED') {
        this.health.remove(topic);
        this.recompute();
      }
    });
    entry.channel = ch;
  }

  subscribe(topic: string, handler: RealtimeHandler): () => void {
    let e = this.entries.get(topic);
    if (e?.dropTimer) { clearTimeout(e.dropTimer); e.dropTimer = null; }
    if (!e) {
      e = { channel: null, handlers: new Set(), erroredAt: null, dropTimer: null };
      this.entries.set(topic, e);
      this.join(topic, e);
    }
    const entry = e;
    entry.handlers.add(handler);
    return () => {
      if (this.entries.get(topic) !== entry) return;
      entry.handlers.delete(handler);
      if (entry.handlers.size === 0 && !entry.dropTimer) {
        entry.dropTimer = setTimeout(() => {
          entry.dropTimer = null;
          if (entry.handlers.size > 0 || this.entries.get(topic) !== entry) return;
          this.entries.delete(topic);
          this.health.remove(topic);
          const ch = entry.channel;
          entry.channel = null;
          if (ch) this.drop(topic, ch);
          this.recompute();
        }, LEAVE_DELAY_MS);
      }
    };
  }

  private armRefusedRetry() {
    if (this.refusedTimer || typeof setInterval === 'undefined') return;
    this.refusedTimer = setInterval(() => {
      const refused = this.health.refused().filter(t => this.entries.has(t));
      if (!refused.length) { clearInterval(this.refusedTimer!); this.refusedTimer = null; return; }
      for (const t of refused) this.join(t, this.entries.get(t)!);
    }, REFUSED_RETRY_MS);
  }

  /**
   * Back on screen, back online, or woken up: make sure the socket is connecting now rather than waiting out its
   * backoff, and rejoin channels that have been stuck errored.
   */
  nudge() {
    if (!supabase || this.entries.size === 0) return;
    if (!supabase.realtime.isConnected()) supabase.realtime.connect();
    const now = Date.now();
    for (const [topic, e] of this.entries) {
      if (e.erroredAt !== null && now - e.erroredAt >= STUCK_CHANNEL_MS && supabase.realtime.isConnected()) {
        const old = e.channel;
        e.channel = null;
        e.erroredAt = null;
        if (old) this.drop(topic, old);
        this.join(topic, e);
      }
    }
  }

  async setAuth(token: string | null) {
    if (supabase && token) await supabase.realtime.setAuth(token);
    // A new token may be allowed topics the old one wasn't.
    if (supabase) for (const t of this.health.refused()) { const e = this.entries.get(t); if (e) this.join(t, e); }
  }

  reset() {
    for (const [t, e] of this.entries) {
      if (e.dropTimer) clearTimeout(e.dropTimer);
      if (e.channel) this.drop(t, e.channel);
      e.channel = null;
      this.health.remove(t);
    }
    this.entries.clear();
    this.recompute();
  }
}

export const realtime = new RealtimeHub();
